import {getRelatedFieldValue} from '../../../_shared/GWTBridge/invoke/priTextBox';
import {getStorageItem, setStorageItem} from '../../../services/storageService';
import {INFO_MAP, FORM_TITLE_MAP, BasicFormData, InfoMap, numOfDefInitialCols} from '../constants';
import {Form} from '../../../_legacy/Form/stores/Form';
import {Column} from '../../../_legacy/Form/serializers/column';
import {getLang} from '../../../Localization';
import {FormData} from '../components/DialogHeader';
import {formsStore} from '../../../_legacy/Form/stores/FormsStore';
import {getPrevIndexedByZoomFname} from '../../Personalization/service/moreInfoPrevIndexed';

export const updateSelectedFields = (
    data: Array<any>,
    entityId: string,
    selectedFields: Array<string>
) => {
    return data.map(item => (item.entityId === entityId ? {...item, selectedFields} : item));
};

export const getZoomFname = (form: Form, column: Column) => {
    let zoomFname = column?.zoomFname;
    if (!zoomFname && column?.relColName) {
        const relcol = form.getColumnByName(column.relColName);
        zoomFname = relcol?.zoomFname;
    }
    return zoomFname;
};

export const isMoreInfoField = (form: Form, column: Column) => {
    const zoomFname = getZoomFname(form, column);
    return Boolean(INFO_MAP[zoomFname]);
};

const stored_map_key = 'stored_more_details_map';
const collapsed_sections_key = 'more_info_collapsed_sections';

export const setCollapsedSection = (data: {[key: string]: Array<string>}) => {
    localStorage.setItem(collapsed_sections_key, JSON.stringify(data));
};

export const getCollapsedSection = () => {
    const localData = localStorage.getItem(collapsed_sections_key);

    return localData ? JSON.parse(localData) : {};
};

export const saveInfoMap = async (mapData: InfoMap) => {
    try {
        const encodedData = btoa(JSON.stringify(mapData));
        await setStorageItem(stored_map_key, encodedData);
    } catch (e) {
        throw new Error(e as string);
    }
};

export const getStoredMap = async () => {
    try {
        const storedObj = await getStorageItem(stored_map_key);
        const data = storedObj?.[stored_map_key] || '';
        return data ? JSON.parse(atob(data)) : null;
    } catch (e) {
        console.error(
            'An error occured while getting stored map',
            (e as Error).message,
            '\n Showing data according to default Info Map'
        );
        return INFO_MAP;
    }
};

export const getInfoMap = async () => {
    try {
        const loadDefault = window.location.search.includes('_defaultInfoMap');
        const storedData = !loadDefault && (await getStoredMap());

        if (!storedData) {
            const encodedData = btoa(JSON.stringify(INFO_MAP));
            await setStorageItem(stored_map_key, encodedData);
            return INFO_MAP;
        } else {
            return storedData.data || storedData;
        }
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

const getFormTitle = (entityId: string) => {
    const lang = getLang() === '1' ? 'he' : 'en';
    return FORM_TITLE_MAP[entityId][lang];
};

const getUnshownDefault = (entityIds: string[], zoomFname: string): BasicFormData[] => {
    const deafultForms: BasicFormData[] = INFO_MAP[zoomFname];
    const unshownDefaults = deafultForms
        .filter(form => !entityIds.includes(form.entityId))
        .map(form => {
            return {...form, formTitle: getFormTitle(form.entityId)};
        });

    return unshownDefaults;
};

export const getAvailableForms = async (entityIds, zoomFname) => {
    const unshownDefaults = getUnshownDefault(entityIds, zoomFname);
    const {currentForm, currentFormName} = formsStore;
    const company = currentForm.formCompanyName;
    const cachedAvailables = formsStore.prevIndexedScreens[`${company}_${currentFormName}`];
    const availableIndexedScreens =
        cachedAvailables || (await getPrevIndexedByZoomFname(zoomFname));
    const availablesExcludingShown = availableIndexedScreens.filter(
        indexedForm => !entityIds.includes(indexedForm.entityId)
    );
    const concatenatedAvailables = [...unshownDefaults, ...availablesExcludingShown];
    const availableForms = concatenatedAvailables.sort((a, b) => {
        if (a.formTitle < b.formTitle) {
            return -1;
        } else if (a.formTitle > b.formTitle) {
            return 1;
        } else {
            return 0;
        }
    });
    return availableForms;
};

export const isRelatedFieldValue = (column: Column) => {
    return column.relColName ? getRelatedFieldValue().length > 0 : true;
};

const findFormInMapSection = (forms: BasicFormData[], formName: string) => {
    const form = forms.find(form => form.entityId === formName);
    return form;
};

export const isScreenInInfoMap = async (zoomFname: string, formName: string) => {
    const isInLocalMap = findFormInMapSection(INFO_MAP[zoomFname], formName);
    if (isInLocalMap) {
        return true;
    }
    const storedMap = await getInfoMap();
    const isInStoredMap = findFormInMapSection(storedMap[zoomFname], formName);
    if (isInStoredMap) {
        return true;
    }
    return false;
};

export const getMappedFields = (section: FormData) => {
    return section.fields.map(rec =>
        section.selectedFields.length === 0
            ? rec
            : section.selectedFields
                  .map(fieldName => rec.find(data => fieldName === data.ColumnName))
                  .filter(Boolean)
    );
};

export const getDataToStore = (data: FormData[]) => {
    return data.map(form => {
        return {
            entityId: form.entityId,
            formName: form.formName,
            subFormName: form.subFormName,
            selectedFields: form.selectedFields,
            template: form.template,
            hasAttachment: form.hasAttachment,
            targetColName: form.targetColName
        };
    });
};

export const getDefaultFields = (selectedEntity: FormData, zoomFname: string): string[] => {
    return (
        INFO_MAP[zoomFname]?.find(item => item.entityId === selectedEntity.entityId)
            ?.selectedFields ||
        selectedEntity.cols?.slice(0, numOfDefInitialCols).map(col => col.Name)
    );
};
